import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import { MatchFlames } from '@/components/applications/MatchFlames';
import { formatCompactNumber } from '@/components/contact/profile/map-creator-profile-to-contact';
import { HStack, Stack, Text } from '@chakra-ui/react';
import type {
  CompanionActionDto,
  CompanionBriefApplicant,
  CompanionBriefApplicationEvidence,
} from '@fluenceur/types';
import { UserPlus } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The "new application" card, as the companion panel shows it.
 *
 * One card per brief. Alone, the applicant is named with the figures actually
 * measured about her and her match read as flames, never as a score; grouped,
 * the server's sentence names two and counts the rest, and the card opens the
 * brief's list rather than singling anyone out. Answering happens on the
 * Candidatures page, never here.
 */

interface BriefApplicationCardProps {
  action: CompanionActionDto & { evidence: CompanionBriefApplicationEvidence };
  /** Opens the Candidatures page. Wired by the panel, never here. */
  onOpenCta: () => void;
  /** Starts the dismissal choice ("Later" among it). Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

const NETWORK_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

/** "@handle · 48.2K Instagram followers · 4.1% engagement", with only what is known. */
const useApplicantFacts = (applicant: CompanionBriefApplicant): string | null => {
  const { t, i18n } = useTranslation();
  const parts: string[] = [];

  if (applicant.handle) parts.push(`@${applicant.handle.replace(/^@/, '')}`);
  if (applicant.followers !== null) {
    parts.push(
      t('companion.cards.briefApplication.followers', {
        followers: formatCompactNumber(applicant.followers, i18n.language),
        network: applicant.network ? (NETWORK_NAMES[applicant.network] ?? applicant.network) : '',
      }).trim()
    );
  }
  if (applicant.engagementRate !== null) {
    parts.push(
      t('companion.cards.briefApplication.engagement', {
        rate: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(
          applicant.engagementRate
        ),
      })
    );
  }

  return parts.length > 0 ? parts.join(' · ') : null;
};

const SingleApplicant = ({ applicant }: { applicant: CompanionBriefApplicant }) => {
  const facts = useApplicantFacts(applicant);
  if (!facts && applicant.matchFlames === null) return null;

  return (
    <Stack gap="6px">
      {facts ? <EvidenceLine>{facts}</EvidenceLine> : null}
      <MatchFlames flames={applicant.matchFlames} />
    </Stack>
  );
};

export const BriefApplicationCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: BriefApplicationCardProps) => {
  const { evidence } = action;
  const single = evidence.applicationCount === 1 ? evidence.applicants[0] : undefined;

  return (
    <CompanionCardShell
      testId="companion-brief-application-card"
      dataAttrs={{ 'data-brief-template-id': evidence.briefTemplateId }}
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader
        title={action.reason}
        eyebrow={
          <HStack gap="6px" color="lilac.700">
            <UserPlus width={13} height={13} aria-hidden="true" />
            <Text
              fontFamily="heading"
              fontSize="10.5px"
              fontWeight="700"
              letterSpacing="0.08em"
              textTransform="uppercase"
            >
              {action.title}
            </Text>
          </HStack>
        }
      />

      {single ? <SingleApplicant applicant={single} /> : null}
    </CompanionCardShell>
  );
};
